import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { TeamService } from '../../services/teamService';
import { CreateTeamRequestData, TeamSettings } from '../../types/team';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { showToast } from '../../utils/toast';
import { JourneyPlatformIntegrationService } from '../../services/journeyPlatformIntegrationService';
import '../../styles/pages/CreateTeamRequestPage.css';

const CreateTeamRequestPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [maxTeamSize, setMaxTeamSize] = useState(10);
  const [formData, setFormData] = useState<CreateTeamRequestData>({
    title: '',
    project_introduction: '',
    team_size: 3,
    min_time_commitment: '',
    skills_required: [],
    additional_requirements: '',
    deadline: ''
  });

  const [newSkill, setNewSkill] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const commonSkills = [
    { key: 'skillReact', value: 'React' },
    { key: 'skillNodeJs', value: 'Node.js' },
    { key: 'skillPython', value: 'Python' },
    { key: 'skillJavaScript', value: 'JavaScript' },
    { key: 'skillTypeScript', value: 'TypeScript' },
    { key: 'skillUIUXDesign', value: 'UI/UX Design' },
    { key: 'skillProjectManagement', value: 'Project Management' },
    { key: 'skillMarketing', value: 'Marketing' },
    { key: 'skillDataAnalysis', value: 'Data Analysis' },
    { key: 'skillMobileDevelopment', value: 'Mobile Development' },
    { key: 'skillBackendDevelopment', value: 'Backend Development' },
    { key: 'skillFrontendDevelopment', value: 'Frontend Development' },
    { key: 'skillDevOps', value: 'DevOps' },
    { key: 'skillDatabaseDesign', value: 'Database Design' },
    { key: 'skillMachineLearning', value: 'Machine Learning' },
    { key: 'skillAI', value: 'AI' },
    { key: 'skillBlockchain', value: 'Blockchain' },
    { key: 'skillCloudComputing', value: 'Cloud Computing' }
  ];

  // Helper function to get translated skill name
  const getSkillTranslation = (skillValue: string): string => {
    const skill = commonSkills.find(s => s.value === skillValue);
    if (skill) {
      return t(`teams.${skill.key}`);
    }
    // For custom skills not in the common list, return as-is
    return skillValue;
  };

  const timeCommitmentOptions = [
    { key: 'timeCommitment2to3Hours', value: '2-3 hours/day' },
    { key: 'timeCommitment4to5Hours', value: '4-5 hours/day' },
    { key: 'timeCommitment6to8Hours', value: '6-8 hours/day' },
    { key: 'timeCommitmentPartTime', value: 'Part-time (10-20 hours/week)' },
    { key: 'timeCommitmentFullTime', value: 'Full-time (40+ hours/week)' },
    { key: 'timeCommitmentFlexible', value: 'Flexible schedule' },
    { key: 'timeCommitmentWeekends', value: 'Weekends only' },
    { key: 'timeCommitmentEvenings', value: 'Evenings only' }
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadSettings();
  }, [user, navigate]);

  useEffect(() => {
    calculateCost();
  }, [formData.team_size]);

  // Load template data if coming from journey workflow
  useEffect(() => {
    const loadTemplate = async () => {
      const templateId = searchParams.get('template');
      const stepId = searchParams.get('stepId');
      const journeyId = searchParams.get('journeyId');
      
      if (stepId && journeyId) {
        try {
          const integrationData = await JourneyPlatformIntegrationService.prepareIntegrationData(
            stepId,
            'team',
            templateId || undefined, // Allow undefined for contextual matching
            journeyId
          );
          
          // Only extract template data if we have a template
          if (integrationData.template) {
            const templateData = JourneyPlatformIntegrationService.extractTemplateData(integrationData.template);
            
            // Pre-fill form with template data
            setFormData(prevData => ({
              ...prevData,
              title: templateData.title || prevData.title,
              project_introduction: templateData.project_introduction || prevData.project_introduction,
              team_size: templateData.team_size || prevData.team_size,
              min_time_commitment: templateData.min_time_commitment || prevData.min_time_commitment,
              skills_required: templateData.skills_required || prevData.skills_required,
              additional_requirements: templateData.additional_requirements || prevData.additional_requirements,
              deadline: templateData.deadline || prevData.deadline
            }));
          }
        } catch (err) {
          console.error('Failed to load template:', err);
          // Don't show error to user, just log it
        }
      }
    };

    loadTemplate();
  }, [searchParams]);

  const loadSettings = async () => {
    try {
      const settings = await TeamService.getTeamSettings();
      const maxSizeSetting = settings.find(s => s.setting_name === 'max_team_size');
      if (maxSizeSetting) {
        setMaxTeamSize(maxSizeSetting.setting_value);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const calculateCost = async () => {
    try {
      const cost = await TeamService.calculateTeamCost(formData.team_size);
      setEstimatedCost(cost);
    } catch (error) {
      console.error('Error calculating cost:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = t('teams.titleRequired');
    }

    if (!formData.project_introduction.trim()) {
      newErrors.project_introduction = t('teams.projectIntroductionRequired');
    }

    if (formData.team_size < 2) {
      newErrors.team_size = t('teams.teamSizeMinimum');
    }

    if (formData.team_size > maxTeamSize) {
      newErrors.team_size = t('teams.teamSizeMaximum', { max: maxTeamSize });
    }

    if (!formData.min_time_commitment) {
      newErrors.min_time_commitment = t('teams.timeCommitmentRequired');
    }

    if (formData.skills_required.length === 0) {
      newErrors.skills_required = t('teams.atLeastOneSkillRequired');
    }

    if (!formData.deadline) {
      newErrors.deadline = t('teams.deadlineRequired');
    } else {
      const deadlineDate = new Date(formData.deadline);
      const now = new Date();
      const oneDay = 24 * 60 * 60 * 1000;
      
      if (deadlineDate <= now) {
        newErrors.deadline = t('teams.deadlineMustBeFuture');
      } else if (deadlineDate.getTime() - now.getTime() < oneDay) {
        newErrors.deadline = t('teams.deadlineMinimum');
      }
    }

    if (!user || user.points < estimatedCost) {
      newErrors.points = t('teams.insufficientPoints', { required: estimatedCost, available: user?.points || 0 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || loading) return;

    setLoading(true);
    try {
      const teamId = await TeamService.createTeamRequest(formData);
      
      // Track completion if this came from a journey workflow
      const stepId = searchParams.get('stepId');
      if (stepId && teamId) {
        try {
          await JourneyPlatformIntegrationService.trackActionCompletion(
            stepId,
            'team',
            teamId
          );
        } catch (trackingError) {
          console.error('Failed to track action completion:', trackingError);
          // Don't fail the whole operation if tracking fails
        }
      }
      
      navigate('/teams', { state: { message: t('teams.teamFormedSuccess', { count: formData.team_size }) } });
    } catch (error) {
      console.error('Error creating team request:', error);
      showToast(t('teams.failedToFormTeam'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const addSkill = (skill: string) => {
    if (skill && !formData.skills_required.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        skills_required: [...prev.skills_required, skill]
      }));
    }
  };

  const removeSkill = (skillToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      skills_required: prev.skills_required.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleAddNewSkill = () => {
    if (newSkill.trim()) {
      addSkill(newSkill.trim());
      setNewSkill('');
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const handleCancel = () => {
    navigate('/teams');
  };

  if (!user) {
    return null; // Will redirect to login
  }
  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="create-team-request-page">
        <div className="form-container">
          <form onSubmit={handleSubmit} className="team-request-form">
            {/* Cost Display */}
            <div className="cost-display">
              <div className="cost-info">
                <span className="cost-label">{t('teams.teamCreationEstimatedCost')}</span>
                <span className="cost-amount">💰 {estimatedCost} {t('teams.points')}</span>
              </div>
              <div className="user-points">
                {t('teams.yourPoints')} {user?.points || 0}
              </div>
            </div>

            {/* Project Title */}
            <div className="form-group">
              <label htmlFor="title">{t('teams.projectTitle')} *</label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('teams.projectTitlePlaceholder')}
                className={errors.title ? 'error' : ''}
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>

            {/* Project Introduction */}
            <div className="form-group">
              <label htmlFor="project_introduction">{t('teams.projectIntroduction')} *</label>
              <textarea
                id="project_introduction"
                value={formData.project_introduction}
                onChange={e => setFormData(prev => ({ ...prev, project_introduction: e.target.value }))}
                placeholder={t('teams.projectIntroductionPlaceholder')}
                rows={4}
                className={errors.project_introduction ? 'error' : ''}
              />
              {errors.project_introduction && <span className="error-message">{errors.project_introduction}</span>}
            </div>

            {/* Team Size and Deadline */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="team_size">{t('teams.teamSizeField')} *</label>
                <input
                  id="team_size"
                  type="number"
                  min="2"
                  max={maxTeamSize}
                  value={formData.team_size}
                  onChange={e => setFormData(prev => ({ ...prev, team_size: parseInt(e.target.value) || 2 }))}
                  className={errors.team_size ? 'error' : ''}
                />
                <small>{t('teams.includingYourself', { max: maxTeamSize })}</small>
                {errors.team_size && <span className="error-message">{errors.team_size}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="deadline">{t('teams.deadlineField')} *</label>
                <input
                  id="deadline"
                  type="date"
                  min={getMinDate()}
                  value={formData.deadline}
                  onChange={e => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                  className={errors.deadline ? 'error' : ''}
                />
                {errors.deadline && <span className="error-message">{errors.deadline}</span>}
              </div>
            </div>

            {/* Time Commitment */}
            <div className="form-group">
              <label htmlFor="min_time_commitment">{t('teams.timeCommitmentField')} *</label>
              <select
                id="min_time_commitment"
                value={formData.min_time_commitment}
                onChange={e => setFormData(prev => ({ ...prev, min_time_commitment: e.target.value }))}
                className={errors.min_time_commitment ? 'error' : ''}
              >
                <option value="">{t('teams.selectTimeCommitment')}</option>
                {timeCommitmentOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {String(t(`teams.${option.key}`) || option.value)}
                  </option>
                ))}
              </select>
              {errors.min_time_commitment && <span className="error-message">{errors.min_time_commitment}</span>}
            </div>

            {/* Required Skills */}
            <div className="form-group">
              <label>{t('teams.requiredSkillsField')} *</label>
              <div className="skills-section">
                <div className="common-skills">
                  <div className="skills-label">{t('teams.commonSkills')}</div>
                  <div className="skills-grid">
                    {commonSkills.map(skill => (
                      <button
                        key={skill.value}
                        type="button"
                        className={`skill-button ${formData.skills_required.includes(skill.value) ? 'selected' : ''}`}
                        onClick={() => 
                          formData.skills_required.includes(skill.value) 
                            ? removeSkill(skill.value) 
                            : addSkill(skill.value)
                        }
                      >
                        {t(`teams.${skill.key}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="custom-skill">
                  <div className="custom-skill-input">
                    <input
                      type="text"
                      placeholder={t('teams.addCustomSkill')}
                      value={newSkill}
                      onChange={e => setNewSkill(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddNewSkill())}
                    />
                    <button type="button" onClick={handleAddNewSkill}>{t('teams.add')}</button>
                  </div>
                </div>

                <div className="selected-skills">
                  <div className="skills-label">{t('teams.selectedSkills')}</div>
                  <div className="selected-skills-list">
                    {formData.skills_required.map(skill => (
                      <span key={skill} className="selected-skill">
                        {getSkillTranslation(skill)}
                        <button type="button" onClick={() => removeSkill(skill)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              {errors.skills_required && <span className="error-message">{errors.skills_required}</span>}
            </div>

            {/* Additional Requirements */}
            <div className="form-group">
              <label htmlFor="additional_requirements">{t('teams.additionalRequirements')}</label>
              <textarea
                id="additional_requirements"
                value={formData.additional_requirements}
                onChange={e => setFormData(prev => ({ ...prev, additional_requirements: e.target.value }))}
                placeholder={t('teams.additionalRequirementsPlaceholder')}
                rows={3}
              />
            </div>

            {/* Points Error */}
            {errors.points && (
              <div className="error-message points-error">
                {errors.points}
              </div>
            )}

            {/* Form Actions */}
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={handleCancel}>
                {t('teams.cancel')}
              </button>
              <button type="submit" className="create-btn" disabled={loading}>
                {loading ? t('teams.creating') : t('teams.createRequestWithCost', { cost: estimatedCost })}
              </button>
            </div>
          </form>
        </div>
      </div>
    </BasePageLayout>
  );
};

export default CreateTeamRequestPage;
